import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Compass, Navigation, MapPin, Search, Loader2, Star, Plus 
} from 'lucide-react';
import { cn } from '../../lib/utils';
import PlaceAutocomplete from '../../components/common/PlaceAutocomplete';
import { POI_TYPES } from './constants';

export const NearbySearchDialog = ({
  open,
  onOpenChange,
  destinationLocation,
  lastActivityLocation,
  nearbyReferenceType,
  setNearbyReferenceType,
  nearbyCustomPlace,
  setNearbyCustomPlace,
  nearbyCategory,
  setNearbyCategory,
  nearbyRadius,
  setNearbyRadius,
  nearbyResults,
  nearbyLoading,
  onSearch,
  onAddPlace,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-orange-500" />
            Buscar opções ao redor
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Encontre restaurantes, hospedagens e atrações
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 min-h-0">
          {/* Reference Point */}
          <div className="space-y-2">
            <Label className="text-xs sm:text-sm">Ponto de referência</Label>
            <Select
              value={nearbyReferenceType}
              onValueChange={setNearbyReferenceType}
            >
              <SelectTrigger data-testid="nearby-reference" className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {destinationLocation && (
                  <SelectItem value="destination">
                    <div className="flex items-center gap-2">
                      <Navigation className="w-4 h-4 text-orange-400" />
                      <span className="truncate">Destino: {destinationLocation.name}</span>
                    </div>
                  </SelectItem>
                )}
                {lastActivityLocation && (
                  <SelectItem value="last_activity">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-400" />
                      <span className="truncate">Última: {lastActivityLocation.name}</span>
                    </div>
                  </SelectItem>
                )}
                <SelectItem value="custom">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-blue-400" />
                    Outro local...
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {nearbyReferenceType === 'custom' && (
              <PlaceAutocomplete
                value={nearbyCustomPlace}
                onChange={setNearbyCustomPlace}
                onSearch={onSearch}
                placeholder="Digite o endereço..."
                showCepButton={false}
                data-testid="nearby-custom-place"
              />
            )}
          </div>

          {/* Category Filter - More compact */}
          <div className="space-y-2">
            <Label className="text-xs sm:text-sm">Categoria</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {POI_TYPES.slice(0, 6).map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setNearbyCategory(value)}
                  className={cn(
                    "flex flex-col items-center gap-1 p-2 rounded-lg text-xs transition-colors",
                    nearbyCategory === value
                      ? "bg-orange-500 text-white"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="truncate w-full text-center">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Radius - Inline */}
          <div className="flex items-center gap-2">
            <Label className="text-xs sm:text-sm flex-shrink-0">Raio:</Label>
            <Select
              value={nearbyRadius.toString()}
              onValueChange={(v) => setNearbyRadius(parseInt(v))}
            >
              <SelectTrigger className="flex-1 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1000">1 km</SelectItem>
                <SelectItem value="3000">3 km</SelectItem>
                <SelectItem value="5000">5 km</SelectItem>
                <SelectItem value="10000">10 km</SelectItem>
                <SelectItem value="20000">20 km</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Search Button inline */}
            <Button
              onClick={onSearch}
              disabled={nearbyLoading}
              className="bg-orange-500 hover:bg-orange-600 h-9 px-4"
              data-testid="nearby-search-submit"
            >
              {nearbyLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Results - Full height available */}
          {(nearbyResults?.length || 0) > 0 && (
            <div className="flex-1 min-h-0 flex flex-col">
              <Label className="text-xs mb-1.5 flex-shrink-0">Resultados ({nearbyResults?.length || 0})</Label>
              <ScrollArea className="flex-1 min-h-[200px]">
                <div className="space-y-1.5 pr-2">
                  {nearbyResults?.map((place) => (
                    <div
                      key={place.place_id}
                      className="bg-slate-800 rounded-lg p-2.5 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{place.name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {place.rating && (
                            <span className="text-[10px] text-yellow-400 flex items-center gap-0.5">
                              <Star className="w-2.5 h-2.5 fill-current" />
                              {place.rating}
                            </span>
                          )}
                          {place.is_open_now !== undefined && (
                            <span className={cn(
                              "text-[10px]",
                              place.is_open_now ? "text-green-400" : "text-red-400"
                            )}>
                              {place.is_open_now ? 'Aberto' : 'Fechado'}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAddPlace(place)}
                        className="bg-orange-500 hover:bg-orange-600 h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
